// useSubscription.ts
// Convenient hook for accessing subscription state and checking access
// Provides memoized access checks and convenience methods

import { useEffect, useMemo, useState } from 'react';
import { useSubscriptionContext } from '../contexts/SubscriptionContext';
import { SubscriptionTier } from '../models/PrivateProfile';
import { PurchasesPackage } from '../services/subscriptionService';
import { isOnboardingComplete } from '../utils/onboardingStorage';

export interface UseSubscriptionReturn {
  // Current state
  tier: SubscriptionTier;
  expiresAt: number | null;
  isLoading: boolean;
  source: string | undefined;
  managementURL: string | null | undefined;
  
  // Access checks
  hasStandardAccess: boolean;
  hasAIAccess: boolean;
  isFree: boolean;
  
  // Tier checks
  checkAccess: (requiredTier: SubscriptionTier) => boolean;
  needsUpgrade: (requiredTier: SubscriptionTier) => boolean;
  
  // Actions
  refreshSubscription: () => Promise<void>;
  purchasePackage: (pkg: PurchasesPackage) => Promise<'success' | 'cancelled' | 'failed'>;
  restorePurchases: (trigger?: 'manual' | 'automatic') => Promise<boolean>;
  showPaywall: () => void;
  
  // Helper strings for UI
  tierDisplayName: string;
  expiryText: string | null;

  // Gating helpers
  hasContentAccess: boolean;
  onboardingComplete: boolean;
}

/**
 * useSubscription
 * Main hook for accessing subscription state and performing access checks
 * 
 * @example
 * ```tsx
 * const { hasAIAccess, tier, showPaywall } = useSubscription();
 * 
 * if (!hasAIAccess) {
 *   return <Button onPress={showPaywall}>Upgrade to AI Premium</Button>;
 * }
 * ```
 */
export const useSubscription = (): UseSubscriptionReturn => {
  const context = useSubscriptionContext();
  const [onboardingComplete, setOnboardingComplete] = useState(false);

  // Check onboarding completion once (content is unlocked after onboarding)
  useEffect(() => {
    let mounted = true;
    isOnboardingComplete()
      .then((result) => {
        if (mounted) setOnboardingComplete(result);
      })
      .catch(() => {
        if (mounted) setOnboardingComplete(false);
      });
    return () => {
      mounted = false;
    };
  }, []);
  
  // Memoized tier checks
  const isFree = useMemo(() => context.tier === 'free', [context.tier]);
  
  /**
   * Check if user has access to a required tier
   * Tiers are hierarchical: ai_premium > standard_premium > free
   */
  const checkAccess = useMemo(() => {
    return (requiredTier: SubscriptionTier): boolean => {
      const tierLevels: Record<SubscriptionTier, number> = {
        free: 0,
        standard_premium: 1,
        ai_premium: 2,
      };
      
      return tierLevels[context.tier] >= tierLevels[requiredTier];
    };
  }, [context.tier]);
  
  /**
   * Check if user needs to upgrade to access a feature
   */
  const needsUpgrade = useMemo(() => {
    return (requiredTier: SubscriptionTier): boolean => {
      return !checkAccess(requiredTier);
    };
  }, [checkAccess]);
  
  /**
   * Get display name for current tier
   */
  const tierDisplayName = useMemo(() => {
    const displayNames: Record<SubscriptionTier, string> = {
      free: 'Free',
      standard_premium: 'Platinum',
      ai_premium: 'AI Platinum',
    };
    return displayNames[context.tier];
  }, [context.tier]);
  
  /**
   * Get formatted expiry text
   */
  const expiryText = useMemo(() => {
    // Null expiresAt with a paid tier = lifetime subscription
    if (!context.expiresAt) {
      if (context.tier === 'ai_premium' || context.tier === 'standard_premium') {
        return 'Lifetime';
      }
      return null;
    }
    
    const expiryDate = new Date(context.expiresAt * 1000);
    const now = new Date();
    
    // Check if expired
    if (expiryDate < now) {
      return 'Expired';
    }
    
    // Calculate days remaining
    const daysRemaining = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysRemaining === 0) {
      return 'Expires today';
    } else if (daysRemaining === 1) {
      return 'Expires tomorrow';
    } else if (daysRemaining < 30) {
      return `Expires in ${daysRemaining} days`;
    } else {
      return expiryDate.toLocaleDateString();
    }
  }, [context.expiresAt, context.tier]);
  
  return {
    // State
    tier: context.tier,
    expiresAt: context.expiresAt,
    isLoading: context.isLoading,
    source: context.source,
    managementURL: context.managementURL,
    
    // Access checks
    hasStandardAccess: context.hasStandardAccess && onboardingComplete,
    hasAIAccess: context.hasAIAccess,
    isFree,
    
    // Tier checks
    checkAccess,
    needsUpgrade,
    
    // Actions
    refreshSubscription: context.refreshSubscription,
    purchasePackage: context.purchasePackage,
    restorePurchases: context.restorePurchases,
    showPaywall: context.showPaywall,
    
    // Display helpers
    tierDisplayName,
    expiryText,

    // Content gating (Pro requires onboarding completion)
    hasContentAccess: context.hasStandardAccess && onboardingComplete,
    onboardingComplete,
  };
};

/**
 * Helper hook for feature-specific access check
 * Returns whether user has access and a function to show upgrade prompt
 * 
 * @example
 * ```tsx
 * const { hasAccess, showUpgrade } = useFeatureAccess('ai_premium');
 * 
 * if (!hasAccess) {
 *   return <UpgradePrompt onUpgrade={showUpgrade} />;
 * }
 * ```
 */
export const useFeatureAccess = (requiredTier: SubscriptionTier) => {
  const { checkAccess, showPaywall } = useSubscription();
  
  const hasAccess = useMemo(() => checkAccess(requiredTier), [checkAccess, requiredTier]);
  
  return {
    hasAccess,
    showUpgrade: showPaywall,
  };
};

